import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  model,
  ModelSignal,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import {
  CustomDynamicFormComponent
} from "frontier/browserkit/src/lib/components/control/form-control/dynamic-form/custom-dynamic-form/custom-dynamic-form.component";
import AppControlGuids from "../../../../api/ControlGuids";
import {
  ConfirmationDialogComponent,
  DialogService,
  EControlActions,
  EsvgFiles,
  IAction,
  IApiRow,
  NewEntryDialogComponent,
  TableControlService, TAnyFormElement,
} from "frontier";
import {
  JSON_EXPORT_ACTION,
  JSON_IMPORT_ACTION
} from "frontier/browserkit/src/lib/components/control/table-control/default-form-actions.constant";
import {
  FormElementComponent
} from "frontier/browserkit/src/lib/components/control/form-control/dynamic-form/form-element/form-element.component";
import {MatDivider} from "@angular/material/divider";
import {
  DynamicFormActionComponent
} from "frontier/browserkit/src/lib/components/control/form-control/dynamic-form/dynamic-form-action/dynamic-form-action.component";
import {HelpIconComponent} from 'frontier/browserkit/src/lib/components/help-icon/help-icon.component';
import {UiHelperService} from 'frontier/browserkit/src/lib/components/ui-helper/ui-helper.service';
import {DistanceCalculationHelpComponent} from './distance-calculation-help/distance-calculation-help.component';
import {MatButton} from '@angular/material/button';
import {catchError, concatMap, filter, finalize, switchMap, tap} from 'rxjs/operators';
import {MatProgressBar} from '@angular/material/progress-bar';
import {TestrulesQueryBuilderComponent,} from "../../testrules-query-builder/testrules-query-builder.component";
import {ConditionTableComponent, IProfile} from "./condition-rules-table.component";
import {AppService, CollectiveService, TProfile} from "../../../../api/generated";
import {MatDialog} from "@angular/material/dialog";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormsModule} from "@angular/forms";
import {MatCheckbox} from "@angular/material/checkbox";
import {RuleModel} from "@syncfusion/ej2-querybuilder";
import {apiTypeSyncFusionTypeMap, testruleCategoryLabels, typeOperatorsMap} from "./verification-rules-mappings";
import {
  EConditionAttributes,
  ETestruleCategory,
  ETestruleFieldType,
  IOperator,
  ITestruleField,
  ITestrulesFormValue,
} from "../../testrules-query-builder/testrules-query-builder-enums-and-interfaces";
import {MatIcon} from "@angular/material/icon";
import {TestRuleSettingsConfigForm} from "./test-rule-settings-config-form/test-rule-settings-config-form";
import {MatList, MatListItem} from "@angular/material/list";
import {DownloadService} from 'frontier/browserkit/src/lib/services/download.service';
import {Observable, of} from 'rxjs';
import {CalculusStore} from "../../../../Services/calculus.store";

enum EFormKeys {
  calcType = 'ttestingsettings;0',
  maxSpeed = 'ttestingsettings;1',
  timeDiff = 'ttestingsettings;2',
  distanceLimit = 'ttestingsettings;3',
}


@Component({
  selector: 'app-verification-rules-config-form',
  standalone: true,
  imports: [FormElementComponent, MatDivider, DynamicFormActionComponent, HelpIconComponent, MatButton, MatProgressBar,
    TestrulesQueryBuilderComponent, ConditionTableComponent, FormsModule, MatCheckbox, MatIcon,
    TestRuleSettingsConfigForm, MatListItem, MatList],
  templateUrl: './verification-rules-config-form.component.html',
  styleUrl: './verification-rules-config-form.component.scss'
})
export class VerificationRulesConfigFormComponent extends CustomDynamicFormComponent {

  protected dialog: MatDialog = inject(MatDialog);
  protected readonly testruleCategoryLabels: Record<ETestruleCategory, string> = testruleCategoryLabels;
  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;
  protected readonly EControlActions: typeof EControlActions = EControlActions;

  private _snackBar: MatSnackBar = inject(MatSnackBar);
  private _uiHelperService: UiHelperService = inject(UiHelperService);
  private _dialogService: DialogService = inject(DialogService);
  private _collectiveService: CollectiveService = inject(CollectiveService);
  private _appService: AppService = inject(AppService);
  private _tableControlService: TableControlService = inject(TableControlService);
  private _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private _downloadService: DownloadService = inject(DownloadService);
  calculusStore: CalculusStore = inject(CalculusStore);

  private readonly fileUpload: Signal<ElementRef> = viewChild<ElementRef>("fileUpload");

  isRecalculating: WritableSignal<boolean> = signal(false);

  EFormKeys: typeof EFormKeys = EFormKeys;

  override GUID: string = AppControlGuids.TTestingSettingsConfig;

  override actions: ModelSignal<IAction[]> = model<IAction[]>([
    JSON_EXPORT_ACTION,
    JSON_IMPORT_ACTION,
  ]);

  formData = {
    calcType: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.calcType)),
    maxSpeed: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.maxSpeed)),
    timeDiff: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.timeDiff)),
    distanceLimit: computed((): TAnyFormElement => this.getFormElementByKey(EFormKeys.distanceLimit)),
  };

  conditionTableRef: Signal<ConditionTableComponent> = viewChild(ConditionTableComponent);

  profileList: WritableSignal<IProfile[]> = signal([]);

  activeProfile: WritableSignal<IProfile | null> = signal(null);

  categoryKeys: ETestruleCategory[] = Object.values(ETestruleCategory);

  testrulesMap: Record<ETestruleCategory, WritableSignal<ITestrulesFormValue>[]> = {
    [ETestruleCategory.trapoError]: [],
    [ETestruleCategory.trapoWarning]: [],
    [ETestruleCategory.checkError]: [],
    [ETestruleCategory.checkWarning]: [],
  };

  testruleRepository: ITestruleField[] = [];

  query: WritableSignal<RuleModel> = signal(null);

  constructor() {
    super();

    this._appService.conditionTablegetRepository().subscribe((res: ITestruleField[]): void => {
      if (res) {
        this.testruleRepository = res.map((table: ITestruleField): ITestruleField => {
          const tableName: string = table.table;
          return {
            ...table,
            field: tableName,
            columns: table.columns.map((column: ITestruleField): ITestruleField => {
              return {
                ...column,
                type: apiTypeSyncFusionTypeMap[column.type as ETestruleFieldType],
                apiType: column.type as ETestruleFieldType,
                operators: typeOperatorsMap[column.type as ETestruleFieldType]
                  .map((op: IOperator) => ({
                      key: op.key,
                      value: op.value,
                    })
                  ),
              };
            }),
          };
        });
      }
    });

    // Filters condition table by currently selected profile
    effect((): void => {
      const activeProfile: IProfile = this.activeProfile();
      if (activeProfile?.guid) {
        this.conditionTableRef().patchFilter({
          searchstring: activeProfile.guid,
        });
      }
    }, {allowSignalWrites: true});
  }

  /**
   * Displays help information for the distance calculation feature.
   *
   * - Opens the handbook at the associated chapter.
   * - Opens the `DistanceCalculationHelpComponent`.
   */
  showHelpForDistanceCalculation(): void {
    this.calculusStore.helpRoute$.next('einstellungen.html#pruefregeln');
    this._uiHelperService.openHelp({component: DistanceCalculationHelpComponent});
  }

  /**
   * Starts, upon user confirmation, the recalculation of all transport distances.
   *
   * - Shows a warning dialog informing the user that the recalculation
   *   may take several minutes and temporarily restrict access to transport data.
   * - Triggers a recalculation.
   * - While the operation is running, disables button to retrigger recalculation via state of `isRecalculating`.
   */
  onRecalculateTransportDistances(): void {
    this._dialogService.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Warnung !',
        description: 'Die Berechnung der Transportdistanzen kann einige Minuten in Anspruch nehmen. ' +
          'In der Zeit können Sie nicht auf alle Fahrten zugreifen! ',
        confirmButtonText: 'Bestätigen',
        cancelButtonText: 'Abbrechen',
      }
    }).afterClosed()
      .pipe(
        tap((): void => this.isRecalculating.set(true)),
        filter((r: any): any => r),
        concatMap((): Observable<boolean> => this._collectiveService.collectiveGetRecalculateAllTransport()),
        finalize((): void => this.isRecalculating.set(false)),
      )
      .subscribe();
  }

  /**
   * Creates a new test rule for a given status category, if allowed by the active profile.
   *
   * - Checks whether the currently active profile is a standard profile.
   * - If it is, shows a snackbar warning indicating that rule creation is not permitted on standard profiles.
   * - If not, sends a request to create a new condition of the given category,
   *   and adds the result via `addNewTestRule()`.
   *
   * @param category The test rule category to assign to the new rule.
   */
  createTestrule(category: ETestruleCategory): void {
    if (this.isCustomProfile()) {
      this._appService.conditionTableNewCondition({
        guid: this.activeProfile().guid,
        cartstate: category,
      }).subscribe((res: any): void => {
        this.addNewTestRule(res);
      });
    } else {
      this._snackBar.open(
        'Sie haben versucht, eine neue Regel an einem Standardprofil hinzuzufügen. Das ist nicht erlaubt.',
        'ok',
        {duration: 5000, verticalPosition: 'top'},
      );
    }
  }

  /**
   * Deletes a test rule from the specified category at the given index, if allowed by the active profile.
   *
   * - Checks whether the currently active profile is a standard profile.
   * - If the profile is a standard profile, shows a snackbar warning.
   * - If not, on user confirm, sends a request to delete the rule from the backend table and
   *   removes the rule from the local `testrulesMap`.
   *
   * @param category The status category of the test rule to delete.
   * @param index The index of the rule within that status category.
   */
  deleteTestrule(category: ETestruleCategory, index: number): void {
    if (this.isCustomProfile()) {
      const testRule: ITestrulesFormValue = this.testrulesMap[category][index]();

      this._dialogService.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: 'Wollen Sie die Regel ' + testRule.description + ' wirklich löschen?',
          description: 'Die Löschung kann nicht rückgängig gemacht werden.',
          confirmButtonText: 'Ja',
          cancelButtonText: 'Nein',
        },
      }).afterClosed()
        .pipe(
          tap((res: any): void => {
            if (res) {
              this._tableControlService.tableControlDeleteLine({
                InstanceId: this.conditionTableRef().instanceId(),
                RowObjects: [testRule.controlObj],
              }).subscribe((deleted: boolean) => {
                if (deleted) {
                  this.testrulesMap[category].splice(index, 1);
                }
              });
            }
          }),
        )
        .subscribe();
    } else {
      this._snackBar.open(
        'Die Regel, die Sie zu löschen versucht haben, gehört zu einem Standardprofil. ' +
        'Solche können nicht gelöscht werden.',
        'ok',
        {duration: 5000, verticalPosition: 'top'},
      );
    }
  }

  /**
   * Duplicates a test rule from the specified category at the given index, if allowed by the active profile.
   *
   * - Checks whether the currently active profile is a standard profile.
   * - If the profile is a standard profile, shows a snackbar warning.
   * - If not, sends a request to duplicate the selected rule via and adds the result via `addNewTestRule()`.

   *
   * @param category The status category of the rule to duplicate.
   * @param index The index of the rule within that status category.
   */
  duplicateTestrule(category: ETestruleCategory, index: number): void {
    if (this.isCustomProfile()) {
      this._appService.conditionTableDuplicateCondition({
        Condition: this.testrulesMap[category][index]().controlObj,
      }).subscribe((res: any): void => {
        this.addNewTestRule(res);
      });
    } else {
      this._snackBar.open(
        'Sie haben versucht, eine neue Regel an einem Standardprofil hinzuzufügen. Das ist nicht erlaubt.',
        'ok',
        {duration: 5000, verticalPosition: 'top'},
      );
    }
  }

  /**
   * Transforms the given raw rule data into a structured `ITestrulesFormValue`
   * and adds it as a reactive signal to the appropriate category in `testrulesMap`.
   *
   * - Constructs a new test rule object from the provided backend response (`row`) and by setting default values.
   * - Wraps the rule in a signal to make it reactive.
   * - Appends the new signal to the `testrulesMap` under the corresponding status category.
   *
   * @param row The raw rule data received from the backend.
   */
  addNewTestRule(row: any): void {
    const transformedRow: ITestrulesFormValue = {
      application: row.application,
      rowidx: -1,
      controlObj: {
        name: row.name,
        type: row.type,
        rowid: row.rowid,
        signature: row.signature,
      },
      guid: row.guid,
      active: row.active,
      description: row.description,
      selected: false,
      parent: "",
    };
    this.testrulesMap[transformedRow.application].push(signal(transformedRow));
  }

  /**
   * Selects the test rule from the given category with the corresponding index.
   *
   * - Sets the selected state of the targeted rule to `true` and of all others to `false`.
   * - Fetches rule's query of the newly selected test rule from the backend.
   * - Deselects any current query (rule) and selects the newly fetched one.
   * - Records `parent` and `ruleGuid`, which will be information required by backend when requesting changes.
   *
   * @param category The status category of the rule to select.
   * @param index The index of the rule within that status category.
   */
  selectTestrule(category: ETestruleCategory, index: number): void {
    // Update selected state
    if (!this.testrulesMap[category][index]().selected) {
      this.query.set(null);
      this._cdr.detectChanges();

      Object.values(this.testrulesMap).forEach((rulesList: WritableSignal<ITestrulesFormValue>[]): void => {
        rulesList.forEach((rule: WritableSignal<ITestrulesFormValue>): void => {
          rule.update((r: ITestrulesFormValue) => ({...r, selected: false}));
        });
      });

      this.testrulesMap[category][index].update((r: ITestrulesFormValue) => ({...r, selected: true}));

      this._appService.conditionTableGetRules({
        InstanceId: this.conditionTableRef().instanceId(),
        condition: this.testrulesMap[category][index]().guid,
      }).subscribe((res: any): void => {
        this.query.set(this.mapOperators({
          condition: res.condition,
          rules: res.rules,
        }));
        this.testrulesMap[category][index].update((r: ITestrulesFormValue) => ({
          ...r,
          parent: res.parent,
          ruleGuid: res.guid,
        }));
      });
    }
  }

  /**
   * Recursively maps a rule object to adapt its type and value for use with the Syncfusion QueryBuilder component.
   *
   * - Converts the internal rule type (enum) to the external Syncfusion field type using a mapping.
   * - Ensures numeric values are cast to `Number` for decimal or integer types.
   * - Recursively applies the same transformation to all nested sub-rules (if present).
   * - Normalizes falsy `value` fields (except `0`) to `null` for compatibility with UI bindings.
   *
   * @param rule A rule object containing field type, value, and optionally nested sub-rules from backend.
   * @returns A transformed `RuleModel` object, including mapped types and normalized values, compatible with
   * SyncFusion's query builder.
   *
   * Note:
   * - The function mutates neither the original object nor the nested rules.
   * - Recursive depth is not limited; deeply nested rules are supported.
   */
  mapOperators(rule: RuleModel): RuleModel {
    const mapped = {
      ...rule,
      type: apiTypeSyncFusionTypeMap[rule.type as ETestruleFieldType],
      value: rule.type === ETestruleFieldType.decimal || rule.type === ETestruleFieldType.integer ? Number(rule.value) : rule.value,
      rules: rule.rules ? rule.rules.map((r: RuleModel): RuleModel => this.mapOperators(r)) : rule.rules,
    };
    if (!mapped.value && mapped.value !== 0) {
      mapped.value = null;
    }
    return mapped;
  }

  /**
   * Opens a dialog to create a new profile and updates the profile list upon confirmation.
   *
   * @remarks
   * - The backend response is assumed to be a complete-updated list of profiles.
   */
  createProfile(): void {
    this.dialog.open(
      NewEntryDialogComponent,
      {data: {edit: false, categoryLabel: 'Profilname'}},
    ).afterClosed().subscribe((res: any): void => {
      if (typeof (res) === 'string') {
        this._appService.conditionTableNewProfile({profile: res}).subscribe((profiles: TProfile[]): void => {
          this.updateProfileList(profiles);
        });
      }
    });
  }

  /**
   * Deletes the currently active profile after user confirmation, if it is not a standard profile.
   *
   * @remarks
   * - Profiles without a `signature` are treated as immutable standard profiles.
   */
  deleteProfile(): void {
    const activeProfile: IProfile = this.activeProfile();
    if (activeProfile != null) {
      if (this.isCustomProfile()) {
        this._dialogService.dialog.open(ConfirmationDialogComponent, {
          data: {
            title: 'Wollen Sie das Profil ' + activeProfile.profile + ' wirklich löschen?',
            description: 'Die Löschung kann nicht rückgängig gemacht werden. ' +
              'Es werden auch alle darin befindlichen Regeln gelöscht.',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein',
          },
        }).afterClosed()
          .pipe(
            tap((res: any): void => {
              if (res) {
                this._appService.conditionTableDeleteProfile({
                  guid: activeProfile.guid,
                }).subscribe((profiles: TProfile[]): void => {
                  this.updateProfileList(profiles);
                });
              }
            }),
          )
          .subscribe();
      } else {
        this._snackBar.open(
          'Das Profil, das Sie zu löschen versucht haben, ist ein Standardprofil. Diese können nicht gelöscht werden.',
          'ok',
          {duration: 5000, verticalPosition: 'top'},
        );
      }
    }
  }

  /**
   * Opens a dialog and renames the currently active profile, if it is not a standard profile.
   *
   * @remarks
   * - Profiles without a `signature` are treated as immutable standard profiles.
   */
  renameProfile(): void {
    const activeProfile: IProfile = this.activeProfile();
    if (activeProfile != null) {
      if (this.isCustomProfile()) {
        this.dialog.open(
          NewEntryDialogComponent,
          {data: {edit: true, categoryLabel: 'Profilname', oldValue: activeProfile.profile}},
        ).afterClosed().subscribe((res: any): void => {
          if (typeof (res) === 'string') {
            this._appService.conditionTableChangeProfile({
              guid: activeProfile.guid,
              name: res,
              inuse: activeProfile.inuse,
            }).subscribe((profiles: TProfile[]): void => {
              this.updateProfileList(profiles);
            });
          }
        });
      } else {
        this._snackBar.open(
          'Das Profil, das Sie umzubenennen versucht haben, ist ein Standardprofil. ' +
          'Diese können nicht umbenannt werden.',
          'ok',
          {duration: 5000, verticalPosition: 'top'},
        );
      }
    }
  }

  /**
   * Duplicates the currently active profile.
   *
   * - If an active profile exists, it sends a request to the backend to duplicate it.
   * - After the duplication succeeds, the updated list of profiles is fetched and
   *   passed to `updateProfileList()` to update the local state.
   *
   * @remarks
   * - This method does not check whether the active profile is a standard profile.
   * - The duplicated profile is expected to be returned as part of the updated profile list.
   */
  duplicateProfile(): void {
    if (this.activeProfile() != null) {
      this._appService.conditionTableDuplicateProfile({guid: this.activeProfile().guid})
        .subscribe((profiles: TProfile[]): void => {
          this.updateProfileList(profiles);
        });
    }
  }

  /**
   * Updates the local profile list and the currently active profile.
   *
   * - Transforms each `TProfile` from the backend into the internal `IProfile` structure and updates the signal
   * profileList with the transformed list.
   * - If an active profile is set, checks whether an up-to-date version of it exists in the new list.
   *   If it differs from the current active profile (based on a deep JSON comparison), the active profile is updated.
   *
   * @param profiles - Array of `TProfile` objects returned from the backend.
   */
  updateProfileList(profiles: TProfile[]): void {
    if (profiles) {
      this.profileList.set(profiles.map((el: TProfile) => {
        return {
          profile: el.name,
          inuse: el.inuse,
          guid: el.guid,
          controlObj: {
            name: el.name,
            type: el.type,
            rowid: el.rowid,
            signature: el.signature,
          },
        };
      }));

      // Update active profile
      const upToDateActiveProfile: IProfile = this.activeProfile() ? this.profileList().find(
        (profile: IProfile): boolean => profile.guid === this.activeProfile().guid) : null;
      if (JSON.stringify(upToDateActiveProfile) !== JSON.stringify(this.activeProfile())) {
        this.activeProfile.set(upToDateActiveProfile);
      }
    }
  }

  /**
   * Sets the currently active profile.
   *
   * @param activeProfile - The profile to set as active.
   *
   * @remarks
   * - No validation is performed on the input; it is assumed to be a valid profile object.
   */
  setActiveProfile(activeProfile: IProfile): void {
    this.activeProfile.set(activeProfile);
  }

  /**
   * Toggles the "in use" status of the given profile and updates the profile list accordingly.
   *
   * - Sends a request to the backend to change the `inuse` flag of the specified profile.
   * - After the backend confirms the change, the updated profile list is fetched and applied.
   *
   * @param toggledProfile - The profile whose `inuse` status should be toggled.
   *
   * @remarks
   * - This method performs a full profile list update after toggling, ensuring state consistency.
   * - It assumes that the backend returns the complete, updated list of profiles.
   */
  toggleInuse(toggledProfile: IProfile): void {
    this._appService.conditionTableChangeProfile({
      guid: toggledProfile.guid,
      name: toggledProfile.profile,
      inuse: !toggledProfile.inuse,
    }).subscribe((profiles: TProfile[]): void => {
      this.updateProfileList(profiles);
    });
  }

  /**
   * Replaces the current test rule map (`testrulesMap`) with new test rules based on the provided rows.
   *
   * - Transforms each incoming API row into the internal `ITestrulesFormValue` format.
   * - Groups the transformed rules by their `application` (status category).
   * - Updates the internal map of rules (`testrulesMap`) with the new grouped data.
   *
   * @param evt - Array of test rule rows returned from an API (of type `IApiRow`).
   *
   * @remarks
   * - The method assumes that each mapped rule has a valid `application` value matching one of the expected categories.
   */
  updateTestrules(evt: IApiRow[]): void {
    let newTestrulesMap: Record<ETestruleCategory, WritableSignal<ITestrulesFormValue>[]> = {
      [ETestruleCategory.trapoError]: [],
      [ETestruleCategory.trapoWarning]: [],
      [ETestruleCategory.checkError]: [],
      [ETestruleCategory.checkWarning]: [],
    };

    evt.forEach((row: IApiRow): void => {
      const transformedRow: ITestrulesFormValue = this.mapTestrule(row);
      newTestrulesMap[transformedRow.application].push(signal(transformedRow));
    });
    this.categoryKeys.forEach((key: ETestruleCategory): void => {
      this.testrulesMap[key] = newTestrulesMap[key];
    })
  }

  /**
   * Maps a raw API row (`IApiRow`) to the internal `ITestrulesFormValue` structure.
   *
   * @param row - The raw test rule data received from the API.
   * @returns An object conforming to `ITestrulesFormValue`, ready to be used in the local state.
   *
   * @remarks
   * - Assumes that `conditionTableRef().columnsInfo()` contains valid indices for `guid`, `application`, `active`, and
   * `description`.
   */
  mapTestrule(row: IApiRow): ITestrulesFormValue {
    return {
      controlObj: row.obj,
      rowidx: row.rowidx,
      guid: row.cols[this.conditionTableRef().columnsInfo().guidIndex].value,
      application: row.cols[this.conditionTableRef().columnsInfo().applicationIndex].value,
      selected: false,
      active: row.cols[this.conditionTableRef().columnsInfo().activeIndex].value,
      description: row.cols[this.conditionTableRef().columnsInfo().descriptionIndex].value,
      parent: "",
    };
  }

  /**
   * Handles a change to a test rule form value and submits the update to the backend,
   * unless the currently active profile is a standard (non-editable) profile.
   *
   * @param evt - Contains the changed value, its attribute key, and the affected form value object.
   *              - `value`: The new value for the attribute (either `string` or `boolean`).
   *              - `attribute`: Enum key indicating which attribute was changed.
   *              - `formValue`: The rule object affected by the change.
   */
  onFormValueChange(evt: {
    value: string | boolean,
    attribute: EConditionAttributes,
    formValue: ITestrulesFormValue,
  }): void {
    if (this.activeProfile() != null) {
      this._tableControlService.tableControlChangeLine({
        InstanceId: this.conditionTableRef().instanceId(),
        Value: evt.value,
        Attribute: evt.attribute,
        AttributeIndex: -1,
        RowObject: evt.formValue.controlObj,
        RowIdx: evt.formValue.rowidx,
      }).subscribe();
    } else {
      this._snackBar.open(
        'Sie haben versucht, eine Regel eines Standardprofils zu bearbeiten. Das ist nicht erlaubt. ' +
        'Bitte duplizieren Sie das Standardprofil und bearbeiten Sie die Kopie.',
        'ok',
        {duration: 5000, verticalPosition: 'top'},
      );
    }
  }

  /**
   * Triggers the export of all condition rules of the current profile and downloads them as a JSON file.
   *
   * - Calls the backend to retrieve the exportable profile data.
   * - Initiates a client-side download of the returned data as a JSON file named "exported_conditions.json".
   *
   * @remarks
   * - The method does not handle errors or provide user feedback on failure.
   */
  onConditionsExport(): void {
    this._appService.conditionTableExportProfile().subscribe((res) => {
      this._downloadService.downloadObjectAsJson(res, "exported_conditions");
    })
  }

  /**
   * Initiates the import process for condition rules by triggering the native file input element.
   *
   * @remarks
   * - This method only opens the file dialog; it does not handle the selected file.
   */
  onConditionsImport() {
    this.fileUpload().nativeElement.click();
  }

  /**
   * Handles the import of condition rules from a user-selected file.
   *
   * - Reads the file content from the file input `change` event.
   * - Parses the file as JSON and sends it to the backend for importing.
   * - Triggers a refresh of the condition table to reflect the newly imported data.
   *
   * @param event - The file input `change` event containing the selected file.
   *
   * @remarks
   * - The uploaded file must contain valid JSON in the expected schema format.
   */
  importConditions(event: Event) {
    this._downloadService.getFileContentFromUploadEvent(event).pipe(
      switchMap((fileContent: string | ArrayBuffer | null) => {
        if (fileContent) {
          const file: object = JSON.parse(fileContent.toString());
          return this._appService.conditionTableImportProfile({importjson: file});
        } else {
          throw new Error('File content is null or undefined');
        }
      }),
      catchError(() => {
        this.feedbackService.setError("Fehler beim Importieren der Prüfregeln");
        return of(null)
      })
    ).subscribe(() => {
      this.feedbackService.setNotification("Prüfregeln erfolgreich importiert");
      this.conditionTableRef().patchFilter({}); // triggers change and fetch to update profile list
    })
  }

  /**
   * Determines whether the currently active profile is a custom (user-created) profile.
   *
   * @returns `true` if the active profile has a non-null signature, indicating it is not a standard profile;
   *          otherwise, `false`.
   *
   * @remarks
   * Standard profiles do not have a signature and are thus not editable by the user.
   */
  isCustomProfile(): boolean {
    return this.activeProfile()?.controlObj?.signature != null;
  }
}
